import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Mail, Phone } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_invoice-tracker-150/artifacts/b42dzl31_SGS%20Logo.png';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    businessType: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert('Thank you for your interest! We will contact you soon.');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      businessType: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Sparkle Grant Solutions" className="h-12 w-auto" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Sparkle Grant Solutions
              </span>
            </div>
            <Link to="/dashboard">
              <Button variant="outline">Invoice System</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xl text-gray-700 mb-8">
              Welcome to Sparkle Grant Solutions, where we provide expert grant writing, procurement support, and business plan development tailored to entrepreneurs, startups, and small businesses. Our team delivers strategic, customized solutions designed to secure funding, support growth, and strengthen long-term success.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Whether you're pursuing funding, refining your strategy, or planning your next phase of growth, Sparkle Grant Solutions is your trusted partner in moving your business forward.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 text-gray-700">
                <Check className="h-5 w-5 text-green-600" />
                <span>$50K - $700K Grants Secured</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Check className="h-5 w-5 text-green-600" />
                <span>3-Month Delivery</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Check className="h-5 w-5 text-green-600" />
                <span>Millions in Procurement Contracts</span>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-6"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Schedule Consultation
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              >
                Our Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Why Choose Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center border-2 hover:border-purple-300 transition-colors">
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Proven Expertise</h3>
                <p className="text-gray-600">Demonstrated success securing grants and procurement contracts</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-purple-300 transition-colors">
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">📝</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Comprehensive Services</h3>
                <p className="text-gray-600">Grant writing, business plans, procurement, and strategic analysis</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-purple-300 transition-colors">
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">💼</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Team</h3>
                <p className="text-gray-600">Our team brings deep expertise in grant writing, budgeting, compliance, and financial management</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-purple-300 transition-colors">
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">🤝</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Payment</h3>
                <p className="text-gray-600">Installment options available</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Comprehensive solutions tailored to your business needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">📝</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Grant Writing</h3>
                <p className="text-gray-600 mb-4">Professional grant writing services, securing grants from private, city, state, and federal level submissions.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Business Plan Writing</h3>
                <p className="text-gray-600 mb-4">Comprehensive and customized business plans tailored to your unique needs and industry.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">🤝</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Procurement & Bids</h3>
                <p className="text-gray-600 mb-4">Navigate the procurement process with expertise.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">📄</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Business Proposals</h3>
                <p className="text-gray-600 mb-4">Winning proposals that capture attention and secure opportunities.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-4xl mb-4">📋</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Annual Reports</h3>
                <p className="text-gray-600 mb-4">Professional annual reports that showcase your organization's achievements and impact.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600">A proven 6-step process to secure your funding</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '💬', title: 'Initial Consultation', desc: '60-minute interview to understand your needs' },
              { icon: '🔍', title: 'Market Research', desc: 'Comprehensive industry and competitor analysis' },
              { icon: '🎯', title: 'Strategy Development', desc: 'Business model and strategic planning' },
              { icon: '📈', title: 'Financial Projections', desc: 'Revenue forecasts, cash flow, and break-even analysis' },
              { icon: '⚙️', title: 'Operational Planning', desc: 'Detailed operational roadmap and execution plan' },
              { icon: '📄', title: 'Executive Summary', desc: 'Professional presentation and submission' }
            ].map((step, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-xl">
                  {index + 1}
                </div>
                <div>
                  <div className="text-3xl mb-2">{step.icon}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Transparent Pricing</h2>
            <p className="text-xl text-gray-600">Flexible payment plans to fit your budget</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-2">
              <CardHeader>
                <div className="text-sm text-gray-600 mb-2">Non-Profit</div>
                <CardTitle className="text-4xl font-bold text-gray-900">$3,000</CardTitle>
                <div className="text-gray-600">3 months engagement</div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Grant research & writing</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Bid submissions</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>3 revisions included</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Bi-weekly progress reports</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Installment payment options</span>
                  </li>
                </ul>
                <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                  Get Started
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
              <CardHeader>
                <div className="text-sm text-gray-600 mb-2">For-Profit</div>
                <CardTitle className="text-4xl font-bold text-gray-900">$5,000</CardTitle>
                <div className="text-gray-600">3 months engagement</div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Comprehensive grant writing</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Business plan development</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Procurement assistance</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Priority support</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span>Flexible payment plans</span>
                  </li>
                </ul>
                <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-8 space-y-2">
            <p className="text-gray-600">All packages include 3 revisions and bi-weekly progress reports</p>
            <p className="text-purple-600 font-semibold">✨ Installment payment options available for enhanced flexibility</p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Leadership Team</h2>
            <p className="text-xl text-gray-600">Experienced professionals dedicated to your funding success</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="text-center hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <span className="text-5xl text-white font-bold">SB</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Shawana Biggs</h3>
                <p className="text-purple-600 font-semibold mb-3">MBA, Co-Founder</p>
                <p className="text-gray-600 mb-4">
                  Results-driven finance and grant writing expert with a track record of securing and maximizing funding.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <Phone className="h-4 w-4 text-purple-600" />
                  <span>Extension 700</span>
                </div>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-6">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <span className="text-5xl text-white font-bold">MF</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Michaelle Farrell</h3>
                <p className="text-purple-600 font-semibold mb-3">PhD, Co-Founder</p>
                <p className="text-gray-600 mb-4">
                  Leading expert in program sustainability, construction, and engineering with a proven record of success.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                  <Phone className="h-4 w-4 text-purple-600" />
                  <span>Extension 701</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Let's Get Started</h2>
            <p className="text-xl text-gray-600">Schedule your free 60-minute consultation today</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="businessType">Business Type *</Label>
                    <Select value={formData.businessType} onValueChange={(value) => setFormData({ ...formData, businessType: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="non-profit">Non-Profit</SelectItem>
                        <SelectItem value="for-profit">For-Profit</SelectItem>
                        <SelectItem value="startup">Startup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message">Tell us about your needs</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-pink-600">
                    Schedule Free Consultation
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Or Contact Us Directly</h3>
              
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Mail className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
                      <a href="mailto:information@sparklegrantsolutions.com" className="text-purple-600 hover:underline block text-lg">
                        information@sparklegrantsolutions.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <Phone className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div className="w-full">
                      <h4 className="font-semibold text-gray-900 mb-3">Main Office</h4>
                      <a href="tel:866-707-7272" className="text-purple-600 hover:underline text-2xl font-bold block mb-4">
                        866-707-7272
                      </a>
                      <div className="space-y-2 text-gray-700">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-purple-600">Extension 700</span>
                          <span>–</span>
                          <span>Shawana Biggs, MBA</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-purple-600">Extension 701</span>
                          <span>–</span>
                          <span>Michaelle Farrell, PhD</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={LOGO_URL} alt="Sparkle Grant Solutions" className="h-10 w-auto" />
                <span className="text-xl font-bold">Sparkle Grant Solutions</span>
              </div>
              <p className="text-gray-400">
                Strategic grant consulting and funding advisory firm dedicated to helping organizations secure, manage, and maximize grant funding with integrity, clarity, and impact.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-gray-400">
                <p className="font-semibold text-white">Main Office: 866-707-7272</p>
                <p className="text-sm">Ext. 700 – Shawana Biggs, MBA</p>
                <p className="text-sm">Ext. 701 – Michaelle Farrell, PhD</p>
                <p className="mt-3">Email: information@sparklegrantsolutions.com</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Grant Writing</li>
                <li>Business Plan Writing</li>
                <li>Procurement & Bids</li>
                <li>Business Proposals</li>
                <li>Annual Reports</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Sparkle Grant Solutions, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
